import { Component, forwardRef } from '@angular/core';
import {
	NG_VALUE_ACCESSOR,
	ControlValueAccessor,
	FormArray,
	FormControl,
	AbstractControl,
} from '@angular/forms';
import { SelfDestruct } from 'src/shared/utilities/self-destruct';
import { Subdivision, SubdivisionStatus } from 'src/shared/api';
import { takeUntil, filter } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { SubdivisionDialogComponent } from 'src/shared/form-models/subdivision/subdivision-dialog/subdivision-dialog.component';

const SUBDIVISION_STATUS_MAP = new Map<SubdivisionStatus, string>([
	[SubdivisionStatus.Active, 'accent'],
	[SubdivisionStatus.Inactive, 'warn'],
]);

@Component({
	selector: 'rpfsa-subdivision-list',
	templateUrl: './subdivision-list.component.html',
	styleUrls: ['./subdivision-list.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => SubdivisionListComponent),
			multi: true,
		},
	],
})
export class SubdivisionListComponent extends SelfDestruct
	implements ControlValueAccessor {
	array = new FormArray([]);

	// Redeclared template variables
	chipColorMap = SUBDIVISION_STATUS_MAP;
	inactive = SubdivisionStatus.Inactive;

	constructor(private readonly dialog: MatDialog) {
		super();
	}

	// model --> view
	writeValue(value: Subdivision[]): void {
		if (!value) {
			value = [];
		}
		value.forEach((v) => {
			const fc = new FormControl(v);
			this.array.push(fc);
		});
	}

	// view --> model
	registerOnChange(fn: (value: Subdivision[]) => void) {
		this.array.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(fn);
	}

	registerOnTouched(fn: () => void) {
		this.onTouched = fn;
	}

	onTouched: () => void = () => {};

	addSubdivision() {
		this.dialog
			.open<SubdivisionDialogComponent, never, AbstractControl>(
				SubdivisionDialogComponent,
				{ width: '450px' }
			)
			.afterClosed()
			.pipe(filter((control) => !!control))
			.subscribe((control) => {
				this.array.insert(0, control);
			});
	}

	editSubdivision(index: number) {
		this.dialog
			.open<SubdivisionDialogComponent, Subdivision, AbstractControl>(
				SubdivisionDialogComponent,
				{
					width: '450px',
					data: this.array.at(index).value,
				}
			)
			.afterClosed()
			.pipe(filter((control) => !!control))
			.subscribe((control) => {
				this.array.setControl(index, control);
			});
	}

	canDeleteSubdivision(subdivision: Subdivision) {
		return subdivision.isAssignedToAJob
			? subdivision.name + ' is assigned to a job and cannot be deleted.'
			: '';
	}
}
